import React, { useId } from "react";
import { dollars } from "../formatMoney";

export function DonutChart({
  segments,
  title,
  subtitle,
  legendSide = false,
  chartOrderLabels = null,
  centerValue = null,
  centerTotal = null,
  showTitle = true,
  legendTextClassName = "text-3xl",
  stableLegendValues = false,
  showZeroValueLegendItems = false,
  chartClassName = "w-64 h-64",
  responsive = false,
  centerValueClassName = "fill-gray-900 text-base font-semibold",
  centerTotalClassName = centerValueClassName,
  centerLabelClassName = "fill-gray-500 text-xs",
  striped = false,
  stripeColors = ["#818cf8", "#d1d5db"],
}) {
  const stripePatternId = `donut-stripes-${useId().replace(/:/g, "")}`;
  const normalizedSegments = segments
    .map((segment) => ({
      ...segment,
      value: Math.max(0, Number(segment.value) || 0),
    }));
  const safeSegments = normalizedSegments.filter((segment) => segment.value > 0);
  const legendSegments = showZeroValueLegendItems
    ? normalizedSegments
    : normalizedSegments.filter(
        (segment) => segment.value > 0 || segment.showZeroValueLegend,
      );
  const chartSegments = chartOrderLabels
    ? [
        ...chartOrderLabels
          .map((label) => safeSegments.find((segment) => segment.label === label))
          .filter(Boolean),
        ...safeSegments.filter(
          (segment) => !chartOrderLabels.includes(segment.label)
        ),
      ]
    : safeSegments;
  const total = safeSegments.reduce((sum, segment) => sum + segment.value, 0);
  const centerTop = centerValue ?? total;
  const centerBottom = centerTotal ?? total;
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div
      className={`rounded-md border border-gray-200 p-5 space-y-4 ${
        responsive
          ? "negotiation-responsive-donut-card min-w-0 overflow-hidden"
          : ""
      }`}
    >
      {showTitle || subtitle ? (
        <div>
          {showTitle ? (
            <h3 className="text-3xl font-semibold text-gray-900">{title}</h3>
          ) : null}
          {subtitle ? <p className="text-2xl text-gray-600 mt-1">{subtitle}</p> : null}
        </div>
      ) : null}
      <div
        className={
          responsive
            ? "negotiation-responsive-donut-content"
            : legendSide
            ? "flex flex-row items-center gap-6 overflow-x-auto"
            : "flex flex-col md:flex-row md:items-center gap-6"
        }
      >
        <svg
          className={`${chartClassName} shrink-0 ${
            responsive ? "negotiation-responsive-donut-visualization" : ""
          }`}
          viewBox="0 0 120 120"
          role="img"
          aria-label={title}
        >
          {striped ? (
            <defs>
              <pattern
                id={stripePatternId}
                width="12"
                height="12"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="12" height="12" fill={stripeColors[1]} />
                <rect width="6" height="12" fill={stripeColors[0]} />
              </pattern>
            </defs>
          ) : null}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="18"
          />
          {striped ? (
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke={`url(#${stripePatternId})`}
              strokeWidth="18"
            />
          ) : total > 0
            ? chartSegments.map((segment) => {
                const dash = (segment.value / total) * circumference;
                const circle = (
                  <circle
                    key={segment.label}
                    cx="60"
                    cy="60"
                    r={radius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth="18"
                    strokeDasharray={`${dash} ${circumference - dash}`}
                    strokeDashoffset={-offset}
                    strokeLinecap="butt"
                    transform="rotate(-90 60 60)"
                  />
                );
                offset += dash;
                return circle;
              })
            : null}
          <text
            x="60"
            y="46"
            textAnchor="middle"
            className={centerValueClassName}
          >
            {dollars(centerTop)}
          </text>
          <text
            x="60"
            y="62"
            textAnchor="middle"
            className={centerLabelClassName}
          >
            of
          </text>
          <text
            x="60"
            y="79"
            textAnchor="middle"
            className={centerTotalClassName}
          >
            {dollars(centerBottom)}
          </text>
        </svg>
        <div
          className={
            responsive
              ? "negotiation-responsive-donut-legend"
              : "grid grid-cols-[minmax(13rem,max-content)_auto] items-center gap-x-8 gap-y-3 flex-1"
          }
        >
          {legendSegments.map((segment) => {
            const percent = total > 0 ? Math.round((segment.value / total) * 100) : 0;

            return (
              <React.Fragment key={segment.label}>
                <div className={`flex items-center gap-3 min-w-0 ${legendTextClassName}`}>
                  <span
                    className="inline-block h-5 w-5 rounded-sm shrink-0"
                    style={{ backgroundColor: segment.color }}
                  />
                  <span className={segment.legendLabelClassName || "text-gray-700"}>
                    {segment.label}
                  </span>
                </div>
                <strong
                  className={`${legendTextClassName} ${
                    segment.legendValueClassName || "text-gray-900"
                  } whitespace-nowrap ${stableLegendValues ? "tabular-nums" : ""}`}
                  style={stableLegendValues ? {
                    // Reserve the total dollar amount and "(100%)" at every slider value.
                    minWidth: `calc(${dollars(centerBottom).length + 6}ch + 1rem)`,
                  } : undefined}
                >
                  {dollars(segment.displayValue ?? segment.value)}
                  <span className="ml-4 font-normal">
                    ({segment.percentLabel ?? `${percent}%`})
                  </span>
                </strong>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DemandAgainstPotChart({
  title,
  demand,
  pot,
  color,
  demandLabel = "Demand",
  remainingLabel = "Pot Left Over",
  overageLabel = "Demand Above Pot",
  totalLabel = "pot",
  chartOrderLabels,
  legendSide = false,
  showSubtitle = true,
  showTitle = true,
  legendTextClassName,
  centerValueClassName,
  centerTotalClassName,
  demandLegendLabelClassName,
  demandLegendValueClassName,
  showPrimaryZeroValueLegendItems = false,
  responsive = false,
}) {
  const numericDemand = Number(demand);
  const hasDemand =
    demand !== null &&
    demand !== undefined &&
    demand !== "" &&
    Number.isFinite(numericDemand);
  const safeDemand = hasDemand ? Math.max(0, numericDemand) : 0;
  const safePot = Math.max(0, Number(pot) || 0);
  const demandWithinPot = Math.min(safeDemand, safePot);
  const potRemaining = Math.max(0, safePot - safeDemand);
  const demandAbovePot = Math.max(0, safeDemand - safePot);
  const percentOfPot = safePot > 0 ? Math.round((safeDemand / safePot) * 100) : 0;

  return (
    <DonutChart
      title={title}
      subtitle={
        showSubtitle
          ? hasDemand
            ? `${dollars(safeDemand)} is ${percentOfPot}% of the ${dollars(safePot)} ${totalLabel}.`
            : `${dollars(Number.NaN)} is --% of the ${dollars(safePot)} ${totalLabel}.`
          : null
      }
      legendSide={legendSide}
      chartOrderLabels={chartOrderLabels}
      centerValue={hasDemand ? safeDemand : Number.NaN}
      centerTotal={safePot}
      showTitle={showTitle}
      responsive={responsive}
      legendTextClassName={legendTextClassName}
      centerValueClassName={centerValueClassName}
      centerTotalClassName={centerTotalClassName}
      striped={!hasDemand}
      stripeColors={[color, "#d1d5db"]}
      segments={[
        {
          label: demandLabel,
          value: demandWithinPot,
          displayValue: hasDemand ? demandWithinPot : Number.NaN,
          percentLabel: hasDemand ? undefined : "--%",
          color,
          showZeroValueLegend: showPrimaryZeroValueLegendItems,
          legendLabelClassName: demandLegendLabelClassName,
          legendValueClassName: demandLegendValueClassName,
        },
        {
          label: remainingLabel,
          value: potRemaining,
          displayValue: hasDemand ? potRemaining : Number.NaN,
          percentLabel: hasDemand ? undefined : "--%",
          color: "#d1d5db",
          showZeroValueLegend: showPrimaryZeroValueLegendItems,
        },
        { label: overageLabel, value: demandAbovePot, color: "#f97316" },
      ]}
    />
  );
}
